import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PublicationSuggestion, type PublicationUnit } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoTip, LabelWithTip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { MetricTipId } from "@/lib/metricsGlossary";

const SINCE = "2026-08-20";

function lifeBadge(life?: string | null) {
  const v = (life || "unknown").toLowerCase();
  const styles: Record<string, string> = {
    flash: "bg-amber-100 text-amber-900",
    medium: "bg-sky-100 text-sky-900",
    evergreen: "bg-emerald-100 text-emerald-900",
    unknown: "bg-stone-100 text-stone-600",
  };
  const tipId: MetricTipId | undefined =
    v === "flash"
      ? "intel_life_flash"
      : v === "medium"
        ? "intel_life_medium"
        : v === "evergreen"
          ? "intel_life_evergreen"
          : undefined;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium", styles[v] || styles.unknown)}>
      {v}
      {tipId ? <InfoTip tipId={tipId} /> : null}
    </span>
  );
}

function platformLabel(u: {
  matched?: boolean;
  ig_id?: string | null;
  fb_id?: string | null;
}) {
  if (u.matched && u.ig_id && u.fb_id) return "IG+FB";
  if (u.ig_id && u.fb_id) return "IG+FB";
  if (u.ig_id) return "IG";
  if (u.fb_id) return "FB";
  return "–";
}

function formatMetricValue(metric: string, value: number) {
  if (metric === "score") return value.toFixed(1);
  return String(Math.round(value));
}

function Thumb({
  url,
  format,
  className,
}: {
  url?: string | null;
  format?: string | null;
  className?: string;
}) {
  const letter = (format || "?").slice(0, 1).toUpperCase();
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={cn("h-14 w-14 shrink-0 rounded object-cover bg-stone-100", className)}
        loading="lazy"
      />
    );
  }
  return (
    <div
      className={cn(
        "flex h-14 w-14 shrink-0 items-center justify-center rounded bg-stone-100 text-sm font-semibold text-stone-500",
        className,
      )}
    >
      {letter}
    </div>
  );
}

function metricNum(u: PublicationUnit, key: "views" | "comments" | "triggers"): number {
  const m = u.metrics || {};
  const d = u.score_detail || {};
  const fromM = Number(m[key] ?? 0);
  if (fromM) return fromM;
  return Number(d[key] ?? 0);
}

function UnitRow({
  unit,
  rank,
  metric,
  value,
}: {
  unit: PublicationUnit;
  rank?: number;
  metric?: string;
  value?: number;
}) {
  const snippet = (unit.content_text || unit.caption || "Brak treści").slice(0, 140);
  return (
    <tr className="border-b border-stone-100 align-top last:border-0">
      <td className="px-2 py-2 text-xs font-semibold tabular-nums text-muted-foreground">
        {rank != null ? `#${rank}` : ""}
      </td>
      <td className="px-2 py-2">
        <Thumb url={unit.thumb_url} format={unit.format} />
      </td>
      <td className="px-2 py-2 text-xs">
        <span className="rounded bg-stone-900 px-1.5 py-0.5 text-white">{unit.format || "?"}</span>
      </td>
      <td className="px-2 py-2">{lifeBadge(unit.life_class)}</td>
      <td className="px-2 py-2 text-xs text-muted-foreground">{platformLabel(unit)}</td>
      <td className="px-2 py-2 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
        {unit.published_at?.slice?.(0, 10) || ""}
      </td>
      <td className="px-2 py-2 text-sm leading-snug min-w-[12rem] max-w-md">{snippet}</td>
      <td className="px-2 py-2 text-right text-xs tabular-nums">{metricNum(unit, "views")}</td>
      <td className="px-2 py-2 text-right text-xs tabular-nums">{metricNum(unit, "comments")}</td>
      <td className="px-2 py-2 text-right text-xs tabular-nums">{metricNum(unit, "triggers")}</td>
      <td className="px-2 py-2 text-right text-sm font-semibold tabular-nums">
        {metric && value != null
          ? formatMetricValue(metric, value)
          : unit.score != null
            ? Number(unit.score).toFixed(1)
            : "–"}
      </td>
      <td className="px-2 py-2 text-xs">
        <div className="flex flex-col gap-1">
          {unit.ig_url ? (
            <a className="underline" href={unit.ig_url} target="_blank" rel="noreferrer">
              IG
            </a>
          ) : null}
          {unit.fb_url ? (
            <a className="underline" href={unit.fb_url} target="_blank" rel="noreferrer">
              FB
            </a>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function UnitsTable({
  rows,
}: {
  rows: Array<{ unit: PublicationUnit; rank?: number; metric?: string; value?: number }>;
}) {
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground">Brak danych w tej kategorii.</p>;
  }
  return (
    <div className="overflow-x-auto rounded border border-stone-200">
      <table className="w-full min-w-[960px] border-collapse text-left">
        <thead className="bg-stone-50 text-[11px] uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-2 py-2 font-medium">#</th>
            <th className="px-2 py-2 font-medium">Mini</th>
            <th className="px-2 py-2 font-medium">Format</th>
            <th className="px-2 py-2 font-medium">
              <LabelWithTip tipId="intel_life_evergreen" label="Życie" />
            </th>
            <th className="px-2 py-2 font-medium">Plat.</th>
            <th className="px-2 py-2 font-medium">Data</th>
            <th className="px-2 py-2 font-medium">Treść</th>
            <th className="px-2 py-2 font-medium text-right">Views</th>
            <th className="px-2 py-2 font-medium text-right">Kom.</th>
            <th className="px-2 py-2 font-medium text-right">
              <LabelWithTip tipId="intel_triggers" label="Trig." />
            </th>
            <th className="px-2 py-2 font-medium text-right">
              <LabelWithTip tipId="intel_score" label="Score" />
            </th>
            <th className="px-2 py-2 font-medium">Linki</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <UnitRow
              key={`${row.rank ?? "u"}-${row.unit.id}`}
              unit={row.unit}
              rank={row.rank}
              metric={row.metric}
              value={row.value}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatHint(format: string, halfLife: number | null | undefined, share48: number | null | undefined) {
  const share = share48 != null ? Math.round(share48 * 100) : null;
  if (share != null && share >= 70) {
    return "Większość zasięgu w pierwszej dobie (flash). Dobry na szybki test, słaby na powtórki.";
  }
  if (share != null && share < 40) {
    return "Dużo zasięgu po starcie (evergreen). Warto wracać stories / ads / republish.";
  }
  if (halfLife != null && halfLife <= 2) {
    return "Krótki half-life: peak zaraz po publikacji.";
  }
  if (halfLife != null && halfLife >= 7) {
    return "Dłuższy half-life: treść żyje dłużej niż weekend.";
  }
  return `Format ${format}: patrz half-life i udział 48h względem innych.`;
}

export default function PublicationIntelPanel() {
  const qc = useQueryClient();
  const [metric, setMetric] = useState("triggers");

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ["publications-units"] });
    void qc.invalidateQueries({ queryKey: ["publications-brief"] });
    void qc.invalidateQueries({ queryKey: ["publications-longtail-format"] });
    void qc.invalidateQueries({ queryKey: ["publications-status-summary"] });
    void qc.invalidateQueries({ queryKey: ["publications-ranking"] });
  };

  const unitsQ = useQuery({
    queryKey: ["publications-units"],
    queryFn: () => api.publicationsUnits({ limit: 40, order: "score" }),
  });
  const briefQ = useQuery({
    queryKey: ["publications-brief"],
    queryFn: () => api.publicationsWeeklyBrief(),
  });
  const byFmtQ = useQuery({
    queryKey: ["publications-longtail-format"],
    queryFn: () => api.publicationsLongTailByFormat(),
  });
  const statusQ = useQuery({
    queryKey: ["publications-status-summary", SINCE],
    queryFn: () => api.publicationsStatusSummary(SINCE),
  });
  const topQ = useQuery({
    queryKey: ["publications-ranking", metric, SINCE],
    queryFn: () => api.publicationsTop({ metric, limit: 10, since: SINCE }),
  });

  const syncM = useMutation({
    mutationFn: () => api.publicationsSync({ days: 14, ingest: true, content: true }),
    onSuccess: invalidateAll,
  });
  const rematchM = useMutation({
    mutationFn: () => api.publicationsRematchContent({ threshold: 0.9 }),
    onSuccess: invalidateAll,
  });

  const items = unitsQ.data?.items || [];
  const brief = briefQ.data;
  const formats = byFmtQ.data?.formats || [];
  const statusRows = statusQ.data?.rows || [];
  const topItems = topQ.data?.items || [];
  const suggestions: PublicationSuggestion[] = brief?.suggestions?.length
    ? brief.suggestions
    : brief?.primary_recommendation
      ? [
          {
            title: "Sugestia tygodnia",
            why: brief.primary_recommendation,
            action: "Zaplanuj następną publikację według tej wskazówki.",
          },
        ]
      : [];

  const metricChips = useMemo(() => {
    const fromApi = topQ.data?.available_metrics;
    if (fromApi?.length) return fromApi;
    return [
      { key: "triggers", label: "Reakcje ManyChat" },
      { key: "views", label: "Wyświetlenia" },
      { key: "comments", label: "Komentarze" },
      { key: "score", label: "Wynik łączny" },
      { key: "purchases", label: "Zakupy (via)" },
      { key: "sessions", label: "Wejścia (via)" },
    ];
  }, [topQ.data?.available_metrics]);

  return (
    <div className="mt-2 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Leading indicators (trigery, komentarze, score, long-tail). Zakupów do pojedynczej rolki
          organicznej nie przypisujemy. Korelacje czasowe:{" "}
          <Link to="/sprzedaz" className="underline">
            Sprzedaż → Treść ↔ zakupy
          </Link>
          .
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => rematchM.mutate()}
            disabled={rematchM.isPending}
          >
            {rematchM.isPending ? "Łączę IG↔FB…" : "Połącz po transkrypcji"}
          </Button>
          <Button size="sm" onClick={() => syncM.mutate()} disabled={syncM.isPending}>
            {syncM.isPending ? "Sync…" : "Sync z dumpów"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Jak z tego korzystać</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-stone-700">
            <li>
              Zacznij od <strong>Sugestii tygodnia</strong>: co nagrać / wzmocnić w najbliższych
              dniach.
            </li>
            <li>
              Sprawdź <strong>Top</strong> w wybranej metryce (domyślnie trigery ManyChat = intencja).
            </li>
            <li>
              Patrz na klasę <strong>życia</strong> (flash vs evergreen): flash = szybki peak;
              evergreen = wracaj do treści później.
            </li>
            <li>
              Na dole <strong>Zdrowie danych</strong>: czy mamy transkrypcje i pary IG↔FB. Bez tego
              ranking jest ślepy.
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Sugestie tygodnia</CardTitle>
          <p className="text-sm text-muted-foreground">
            Max 3 actionable wskazówki z briefu WoW. To nie są gwarancje sprzedaży.
          </p>
        </CardHeader>
        <CardContent>
          {briefQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Ładowanie briefu…</p>
          ) : !suggestions.length ? (
            <p className="text-sm text-muted-foreground">
              Brak sugestii. Uruchom Sync albo poczekaj na harvest Graph.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              {suggestions.map((s, i) => (
                <div
                  key={`${s.title}-${i}`}
                  className="flex flex-col gap-2 rounded border border-stone-200 p-3"
                >
                  <div className="flex gap-2">
                    <Thumb url={s.thumb_url} format={s.format} className="h-12 w-12" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium leading-snug">{s.title}</div>
                      {s.score != null ? (
                        <div className="text-xs text-muted-foreground">score {s.score}</div>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-xs leading-snug text-muted-foreground">{s.why}</p>
                  <p className="text-sm leading-snug">{s.action}</p>
                  <div className="mt-auto flex flex-wrap gap-2 pt-1 text-xs">
                    {s.ig_url ? (
                      <a className="underline" href={s.ig_url} target="_blank" rel="noreferrer">
                        Instagram
                      </a>
                    ) : null}
                    {s.fb_url ? (
                      <a className="underline" href={s.fb_url} target="_blank" rel="noreferrer">
                        Facebook
                      </a>
                    ) : null}
                    <Link className="underline" to="/karuzele">
                      Kreator
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            <LabelWithTip tipId="intel_score" label="Jak liczymy score" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-stone-700">
          <p>
            Proxy intencji, nie atrybucja sprzedaży:{" "}
            <code className="rounded bg-stone-100 px-1 text-xs">
              trigery×12 + komentarze×0.15 + views/5000 + likes×0.02
            </code>
            , plus +2 gdy treść gotowa, +3 evergreen / +1 flash.
          </p>
          <p className="text-xs text-muted-foreground">
            Trigery = słowa jak „mama” / „pakiet” w komentarzach (leading ManyChat), nie live bot.
            Zakupy (via) w Top dotyczą tylko karuzel ze studia z linkiem.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Top 10</CardTitle>
          <p className="text-sm text-muted-foreground">
            {topQ.data?.label || "Ranking publikacji w wybranej kategorii"} · od {SINCE}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {metricChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => setMetric(chip.key)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  metric === chip.key
                    ? "border-stone-900 bg-stone-900 text-white"
                    : "border-stone-200 bg-white text-stone-700 hover:border-stone-400",
                )}
              >
                {chip.label}
              </button>
            ))}
          </div>
          {(metric === "purchases" || metric === "sessions") && (
            <p className="text-xs text-muted-foreground">
              Tylko karuzele ze studia z linkiem via. Organicznym rolkom nie przypisujemy zakupów.
            </p>
          )}
          {topQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Ładowanie rankingu…</p>
          ) : (
            <UnitsTable
              rows={topItems.map((row) => ({
                unit: row.unit,
                rank: row.rank,
                metric: row.metric,
                value: row.value,
              }))}
            />
          )}
        </CardContent>
      </Card>

      {formats.length ? (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Long-tail po formacie</h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {formats.map((f) => (
              <Card key={f.format}>
                <CardContent className="space-y-1 pt-4 text-sm">
                  <div className="font-medium capitalize">{f.format}</div>
                  <div className="text-muted-foreground">n={f.n}</div>
                  <div className="text-xs">
                    half-life: {f.avg_half_life_days ?? "–"} d · 48h:{" "}
                    {f.avg_share_48h != null ? `${Math.round(f.avg_share_48h * 100)}%` : "–"}
                  </div>
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    {formatHint(f.format, f.avg_half_life_days, f.avg_share_48h)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Wszystkie jednostki (score)</h3>
        {unitsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Ładowanie…</p>
        ) : !items.length ? (
          <p className="text-sm text-muted-foreground">
            Brak jednostek. Kliknij Sync albo poczekaj na harvest Graph (Celery).
          </p>
        ) : (
          <UnitsTable rows={items.map((u) => ({ unit: u }))} />
        )}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Zdrowie danych</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {statusQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Liczenie statusu…</p>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {statusRows.map((row) => (
                  <div key={row.key} className="rounded border border-stone-200 px-3 py-2">
                    <div className="text-xs text-muted-foreground">{row.label}</div>
                    <div className="text-xl font-semibold tabular-nums">{row.value}</div>
                    {row.hint ? (
                      <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{row.hint}</p>
                    ) : null}
                  </div>
                ))}
              </div>
              {statusQ.data?.note ? (
                <p className="text-xs text-muted-foreground">{statusQ.data.note}</p>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
